import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
  themeQuartz,
} from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { Currency } from "@/gql/graphql";
import dayjs from "dayjs";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  ReportInvoiceListItem,
  reportInvoiceListQuery,
} from "@/app/Reports/Reports/Components/ReportInvoices/GraphQL/reportInvoicesQuery";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { InvoicePaymentDetails } from "./InvoicePaymentDetails";
import { convertToAUD } from "./utils/currencyConversion";
import { useTranslations } from "use-intl";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

export function ReportInvoicesTable({ ref }: { ref: any }) {
  const t = useTranslations("payment");
  const { data, isLoading, isError } = useGqlQuery(reportInvoiceListQuery, {
    rangeStartDate: "1900-01-01",
    rangeEndDate: "2099-12-31",
  });

  const formatSummaryAmount = useCallback((params: any) => {
    const currency = params.data?.currency ?? Currency.Aud;
    const formattedAmount = formatCurrency(params.value, currency, {
      emptyValue: "",
    });

    // Add ~ symbol if this is a summary row with multiple currencies
    if (params.data?._isSummaryRow && params.data?._hasMultipleCurrencies) {
      return `~${formattedAmount}`;
    }

    return formattedAmount;
  }, []);

  const columns = useMemo(() => {
    return [
      {
        headerName: "",
        field: "expand" as any,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          suppressCount: true,
          suppressDoubleClickExpand: true,
          innerRenderer: () => null,
        },
        width: 50,
        pinned: "left",
        lockPosition: true,
        suppressMovable: true,
      },
      {
        field: "invoice_reference",
        headerName: "Invoice #",
        filter: "agTextColumnFilter",
        pinned: "left",
      },
      {
        field: "customer_name",
        headerName: "Customer Name",
        filter: "agTextColumnFilter",
      },
      {
        field: "invoice_date",
        headerName: "Date of Invoice",
        valueGetter: (params) => {
          if (!params.data?.invoice_date) return null;
          return dayjs.utc(params.data.invoice_date).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
            if (!cellValue) return -1;
            const normalizedFilterDate = dayjs(
              filterLocalDateAtMidnight,
            ).startOf("day");
            const normalizedCellDate = dayjs(cellValue).startOf("day");

            if (normalizedFilterDate.isBefore(normalizedCellDate, "day"))
              return 1;
            if (normalizedFilterDate.isAfter(normalizedCellDate, "day"))
              return -1;
            return 0;
          },
        },
      },
      {
        field: "date_paid",
        headerName: "Date Paid",
        valueGetter: (params) => {
          if (!params.data?.date_paid) return null;
          return dayjs.utc(params.data.date_paid).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
            if (!cellValue) return -1;
            const normalizedFilterDate = dayjs(
              filterLocalDateAtMidnight,
            ).startOf("day");
            const normalizedCellDate = dayjs(cellValue).startOf("day");

            if (normalizedFilterDate.isBefore(normalizedCellDate, "day"))
              return 1;
            if (normalizedFilterDate.isAfter(normalizedCellDate, "day"))
              return -1;
            return 0;
          },
        },
      },
      {
        field: "payment_method",
        headerName: "Method of Payment",
        filter: "agSetColumnFilter",
        enableRowGroup: true,
        valueFormatter: (params) =>
          params.value ? t(`method.${params.value}`) : null,
      },
      {
        field: "currency",
        headerName: "Currency",
        filter: "agSetColumnFilter",
        enableRowGroup: true,
      },
      {
        field: "amount_inc_tax",
        headerName: "Amount (Inc. Tax)",
        aggFunc: "sum",
        valueFormatter: formatSummaryAmount,
      },
      {
        field: "amount_paid",
        headerName: "Amount Paid",
        aggFunc: "sum",
        valueFormatter: formatSummaryAmount,
      },
      {
        field: "amount_due",
        headerName: "Amount Due",
        aggFunc: "sum",
        valueFormatter: formatSummaryAmount,
        cellRenderer: (params: ICellRendererParams) => {
          const amount = params.value || 0;
          const formattedAmount = formatSummaryAmount(params);

          if (amount > 0) {
            return (
              <span className="font-medium text-red-600">
                {formattedAmount}
              </span>
            );
          }

          return <span>{formattedAmount}</span>;
        },
      },
      {
        field: "invoice_status",
        headerName: "Status",
        filter: "agSetColumnFilter",
        enableRowGroup: true,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value === undefined) {
            return null;
          }

          const status = params.value as string;
          let colorClass = "bg-gray-100 text-gray-800 border-gray-200";

          switch (status.toUpperCase()) {
            case "PAID":
              colorClass = "bg-green-100 text-green-800 border-green-200";
              break;
            case "OVERDUE":
              colorClass = "bg-red-100 text-red-800 border-red-200";
              break;
            case "PENDING":
              colorClass = "bg-yellow-100 text-yellow-800 border-yellow-200";
              break;
            case "DRAFT":
              colorClass = "bg-gray-100 text-gray-800 border-gray-200";
              break;
          }

          const badgeClass = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`;

          return <div className={badgeClass}>{status}</div>;
        },
      },
      {
        field: "managed_by_name",
        headerName: "Organisation",
        filter: "agSetColumnFilter",
        enableRowGroup: true,
      },
    ] as ColDef<ReportInvoiceListItem>[];
  }, []);

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const getPinnedBottomRowData = useCallback(() => {
    if (!data?.reportInvoices || data.reportInvoices.length === 0) {
      return [];
    }

    const totalInvoices = data.reportInvoices.length;

    // Convert all amounts to AUD for totals
    const totalAmountIncTaxAUD = data.reportInvoices.reduce((sum, invoice) => {
      const amountInAUD = convertToAUD(
        invoice.amount_inc_tax || 0,
        invoice.currency as Currency,
      );
      return sum + amountInAUD;
    }, 0);

    const totalAmountPaidAUD = data.reportInvoices.reduce((sum, invoice) => {
      const amountInAUD = convertToAUD(
        invoice.amount_paid || 0,
        invoice.currency as Currency,
      );
      return sum + amountInAUD;
    }, 0);

    const totalAmountDueAUD = data.reportInvoices.reduce((sum, invoice) => {
      const amountInAUD = convertToAUD(
        invoice.amount_due || 0,
        invoice.currency as Currency,
      );
      return sum + amountInAUD;
    }, 0);

    // Check if we have multiple currencies
    const uniqueCurrencies = [
      ...new Set(data.reportInvoices.map((inv) => inv.currency)),
    ];
    const hasMultipleCurrencies = uniqueCurrencies.length > 1;

    return [
      {
        invoice_reference: `Total Invoices: ${totalInvoices}`,
        amount_inc_tax: totalAmountIncTaxAUD,
        amount_paid: totalAmountPaidAUD,
        amount_due: totalAmountDueAUD,
        currency: Currency.Aud,
        _isSummaryRow: true,
        _hasMultipleCurrencies: hasMultipleCurrencies,
      },
    ];
  }, [data?.reportInvoices]);

  const getRowStyle = useCallback((params: any) => {
    if (params.data?._isSummaryRow) {
      return {
        backgroundColor: "#f3f4f6",
      };
    }
    return undefined;
  }, []);

  const isRowMaster = useCallback((dataItem: ReportInvoiceListItem) => {
    return dataItem.payments && dataItem.payments.length > 0;
  }, []);

  const detailCellRenderer = useCallback((params: any) => {
    return <InvoicePaymentDetails invoice={params.data} />;
  }, []);

  if (isError) {
    return <div>An error occurred while retrieving report</div>;
  }

  return (
    <AgGridReact<ReportInvoiceListItem>
      className="grow"
      ref={ref}
      rowData={data?.reportInvoices ?? []}
      loading={isLoading}
      rowSelection={rowSelection}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      pinnedBottomRowData={getPinnedBottomRowData()}
      getRowStyle={getRowStyle}
      masterDetail={true}
      isRowMaster={isRowMaster}
      detailCellRenderer={detailCellRenderer}
      detailRowHeight={250}
    />
  );
}
